import type {
    AutoSuggestInputProps,
    AutoSuggestSuggestion,
    SelectedSuggestion,
} from '@rio-cloud/rio-uikit/components/autosuggest/AutoSuggest';
import AutoSuggest from '@rio-cloud/rio-uikit/components/autosuggest/AutoSuggest';
import useStateWithValidation from '@rio-cloud/rio-uikit/useStateWithValidation';
import type React from 'react';
import { useCallback, useMemo, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { PartnerCreationFlowIcon } from '../../components/PartnerCreationFlowIcon';
import type { CreationFlow, RenterPartnerCompany } from '../../domain/model/rental-companies.types';
import useIsSupportAdministrationRoute from '../../hooks/useIsSupportAdministrationRoute';
import { PARTNERS_QUERY_PARAM_SHOW_CREATE_DIALOG, PARTNERS_ROUTE } from '../../routes/Router';
import type { Schemas as PartnerNetworkSchemas } from '../../services/api/generated/partner-network-api';
import { useFetchAccountProfilesQuery } from '../../services/api/partnerNetwork.api';

type RenterCompanyAutoSuggestionProps = {
    id: string;
    renterPartnerCompanies: RenterPartnerCompany[];
    onChanged(accountId?: string | undefined | null, selection?: RenterCompanySelection): void;
};

export type RenterCompanySelection = {
    accountName?: string;
    source: 'vat-account';
};

type RenterCompanySuggestion = AutoSuggestSuggestion & {
    accountId?: string;
    accountName?: string;
    creationFlow?: CreationFlow;
    suggestionType?:
        | 'partner'
        | 'vat-account'
        | 'add-partner'
        | 'add-partner-divider'
        | 'validation-info'
        | 'validation-warning';
    vatId?: string;
    messageId?: string;
};

type SelectedVatAccount = {
    accountId: string;
    accountName: string;
    displayValue: string;
    vatId: string;
};

type AccountProfile = PartnerNetworkSchemas.AccountProfile;

const ADD_PARTNER_ACCOUNT_ID = 'Add partner';
const VAT_ID_PATTERNS = [
    /^ATU\d{8}$/,
    /^BE0?\d{9}$/,
    /^BG\d{9,10}$/,
    /^CY\d{8}[A-Z]$/,
    /^CZ\d{8,10}$/,
    /^DE\d{9}$/,
    /^DK\d{8}$/,
    /^EE\d{9}$/,
    /^EL\d{9}$/,
    /^ES[A-Z0-9]\d{7}[A-Z0-9]$/,
    /^FI\d{8}$/,
    /^FR[A-Z0-9]{2}\d{9}$/,
    /^GB(\d{9}(\d{3})?|GD\d{3}|HA\d{3})$/,
    /^HR\d{11}$/,
    /^HU\d{8}$/,
    /^IE(\d{7}[A-Z]{1,2}|\d[A-Z]\d{5}[A-Z])$/,
    /^IT\d{11}$/,
    /^LT(\d{9}|\d{12})$/,
    /^LU\d{8}$/,
    /^LV\d{11}$/,
    /^MT\d{8}$/,
    /^NL\d{9}B\d{2}$/,
    /^PL\d{10}$/,
    /^PT\d{9}$/,
    /^RO\d{2,10}$/,
    /^SE\d{12}$/,
    /^SI\d{8}$/,
    /^SK\d{10}$/,
    // TV-prefixed VAT IDs are used by RIO test accounts.
    /^TV\d{8}$/,
    /^XI(\d{9}(\d{3})?|GD\d{3}|HA\d{3})$/,
];

/**
 * Component for auto-suggestion of renter company
 * @param id - identification of the AutoSuggest component
 * @param renterPartnerCompanies - list of renter partner companies
 * @param onChanged - callback function to be called when a valid account is selected by a regular user otherwise null.
 *                    If the user is an Azure user, the value is passed as is.
 * @constructor
 */
const RenterCompanyAutoSuggestion = ({ id, renterPartnerCompanies, onChanged }: RenterCompanyAutoSuggestionProps) => {
    const intl = useIntl();
    const navigate = useNavigate();
    const isSupportAdministrationRoute = useIsSupportAdministrationRoute();
    const selectedVatAccountRef = useRef<SelectedVatAccount | null>(null);

    const allPartnerSuggestions = useMemo(
        () => createPartnerSuggestions(renterPartnerCompanies),
        [renterPartnerCompanies]
    );

    const validateInput = useCallback(
        (newValue: string) => {
            const normalizedInput = getVatIdForLookup(newValue, selectedVatAccountRef.current);

            return (
                normalizedInput.length === 0 ||
                isValidVatId(normalizedInput) ||
                findMatchingPartners(allPartnerSuggestions, newValue).length > 0
            );
        },
        [allPartnerSuggestions]
    );

    const [value, setValue, isValueValid] = useStateWithValidation<string>(validateInput, '');
    const lookupVatId = getVatIdForLookup(value, selectedVatAccountRef.current);
    const hasValidVatFormat = isValidVatId(lookupVatId);
    const { currentData: activeVatAccountProfileList, isFetching: isVatAccountLookupLoading } =
        useFetchAccountProfilesQuery({ query: { vat_id: lookupVatId } }, { skip: !hasValidVatFormat });

    const partnerSuggestions = useMemo(
        () => findMatchingPartners(allPartnerSuggestions, value),
        [allPartnerSuggestions, value]
    );

    const vatAccountSuggestions = useMemo(() => {
        if (!activeVatAccountProfileList || !hasValidVatFormat) {
            return [];
        }

        return activeVatAccountProfileList.items.map(accountProfile =>
            createVatAccountSuggestion(accountProfile, lookupVatId)
        );
    }, [activeVatAccountProfileList, hasValidVatFormat, lookupVatId]);
    const showValidationInfo = !isValueValid;
    const showNoMatchingVatFoundWarning =
        hasValidVatFormat &&
        activeVatAccountProfileList &&
        !isVatAccountLookupLoading &&
        vatAccountSuggestions.length === 0;
    const suggestions = useMemo(() => {
        if (hasValidVatFormat) {
            const suggestionsWithoutAddPartner = [
                ...(showNoMatchingVatFoundWarning ? [createVatLookupWarningSuggestion()] : []),
                ...preferExistingPartnersOverVatAccounts(vatAccountSuggestions, allPartnerSuggestions),
            ];

            return showNoMatchingVatFoundWarning && !isSupportAdministrationRoute
                ? appendAddPartnerSuggestion(
                      suggestionsWithoutAddPartner,
                      'intl-msg:rental-web.dialog.start-rental.renter-company-auto-suggestion.add-partner-via-email-label'
                  )
                : suggestionsWithoutAddPartner;
        }

        const suggestionsWithoutAddPartner = [
            ...(showValidationInfo ? [createValidationInfoSuggestion()] : []),
            ...mergePartnerAndVatAccountSuggestions(partnerSuggestions, vatAccountSuggestions, allPartnerSuggestions),
        ];

        return isSupportAdministrationRoute
            ? suggestionsWithoutAddPartner
            : appendAddPartnerSuggestion(
                  suggestionsWithoutAddPartner,
                  'intl-msg:rental-web.dialog.start-rental.renter-company-auto-suggestion.add-partner-via-email-label'
              );
    }, [
        allPartnerSuggestions,
        activeVatAccountProfileList,
        hasValidVatFormat,
        isSupportAdministrationRoute,
        partnerSuggestions,
        showValidationInfo,
        showNoMatchingVatFoundWarning,
        vatAccountSuggestions,
    ]);

    const handleSuggestionsFetchRequested = ({ value: newValue }: { value: string }) => {
        setValue(newValue);

        const matchingPartners = findMatchingPartners(allPartnerSuggestions, newValue);
        const selectedVatAccount = selectedVatAccountRef.current;

        if (isSupportAdministrationRoute) {
            selectedVatAccountRef.current = null;
            onChanged(newValue);
        } else if (matchingPartners.length === 1 && newValue === matchingPartners[0].label) {
            selectedVatAccountRef.current = null;
            onChanged(matchingPartners[0].accountId as string);
        } else if (
            selectedVatAccount &&
            (newValue === selectedVatAccount.displayValue || selectedVatAccount.vatId === normalizeVatId(newValue))
        ) {
        } else {
            selectedVatAccountRef.current = null;
            onChanged(null);
        }
    };

    const inputProps: AutoSuggestInputProps = {
        placeholder: intl.formatMessage({
            id: 'intl-msg:rental-web.dialog.start-rental.renter-company-auto-suggestion.vat-lookup-placeholder',
        }),
        value,
    };

    const leadingInputAddons = useMemo(
        () => [
            <div key='renter-company-icon' className='input-group-addon'>
                <span className='uikit-icon icon-building' />
            </div>,
        ],
        []
    );

    const partnersPath = `${PARTNERS_ROUTE}?${PARTNERS_QUERY_PARAM_SHOW_CREATE_DIALOG}=true`;
    const onSuggestionsSelected = (
        _event: React.MouseEvent<HTMLLIElement> | React.KeyboardEvent<HTMLDivElement>,
        selectedSuggestion: SelectedSuggestion
    ) => {
        const suggestion = selectedSuggestion.suggestion as RenterCompanySuggestion;

        if (suggestion.disabled) {
            return;
        }

        if (suggestion.accountId === ADD_PARTNER_ACCOUNT_ID) {
            selectedVatAccountRef.current = null;
            navigate(partnersPath);
            return;
        }

        if (suggestion.suggestionType === 'vat-account') {
            const selectedVatAccount = {
                accountId: suggestion.accountId as string,
                accountName: suggestion.accountName as string,
                displayValue: createVatAccountDisplayValue(
                    suggestion.vatId as string,
                    suggestion.accountName as string
                ),
                vatId: suggestion.vatId as string,
            };
            selectedVatAccountRef.current = selectedVatAccount;
            setValue(selectedVatAccount.displayValue);
            onChanged(selectedVatAccount.accountId, {
                accountName: selectedVatAccount.accountName,
                source: 'vat-account',
            });
            return;
        }

        selectedVatAccountRef.current = null;
        onChanged(suggestion.accountId as string);
    };

    return (
        <div className='form-group margin-bottom-0'>
            <div>
                <AutoSuggest
                    id={id}
                    inputProps={inputProps}
                    suggestions={suggestions}
                    leadingInputAddons={leadingInputAddons}
                    onSuggestionsFetchRequested={handleSuggestionsFetchRequested}
                    getSuggestionValue={getRenterCompanySuggestionValue}
                    renderSuggestion={renderRenterCompanySuggestion}
                    onSuggestionSelected={onSuggestionsSelected}
                    isLoading={isVatAccountLookupLoading}
                    openOnFocus
                    data-testid='renter-company-auto-suggest'
                />
            </div>
        </div>
    );
};

const getRenterCompanySuggestionValue = (suggestion: AutoSuggestSuggestion): string => {
    const renterCompanySuggestion = suggestion as RenterCompanySuggestion;

    if (renterCompanySuggestion.suggestionType === 'vat-account') {
        return createVatAccountDisplayValue(
            renterCompanySuggestion.vatId as string,
            renterCompanySuggestion.accountName as string
        );
    }

    return renterCompanySuggestion.label ?? '';
};

const renderRenterCompanySuggestion = (suggestion: AutoSuggestSuggestion) => {
    const renterCompanySuggestion = suggestion as RenterCompanySuggestion;

    switch (renterCompanySuggestion.suggestionType) {
        case 'validation-info':
            return <ValidationInfoMessage />;
        case 'validation-warning':
            return <VatLookupWarningMessage />;
        case 'add-partner-divider':
            return <AddPartnerDivider />;
        case 'add-partner':
            return <AddPartnerAction messageId={renterCompanySuggestion.messageId as string} />;
        case 'vat-account':
            return (
                <VatAccountAction
                    accountName={renterCompanySuggestion.accountName as string}
                    vatId={renterCompanySuggestion.vatId as string}
                />
            );
        case 'partner':
            return renterCompanySuggestion.creationFlow ? (
                <PartnerCreationFlow
                    name={renterCompanySuggestion.label as string}
                    creationFlow={renterCompanySuggestion.creationFlow}
                />
            ) : (
                renterCompanySuggestion.label
            );
        default:
            return renterCompanySuggestion.label;
    }
};

const setAddPartnerDividerRef = (divider: HTMLSpanElement | null) => {
    divider?.closest('a')?.classList.add('margin-0', 'padding-top-0', 'padding-bottom-0');
};

const AddPartnerDivider = () => (
    <span
        ref={setAddPartnerDividerRef}
        className='display-block border border-top-only border-color-lighter'
        data-testid='add-partner-divider'
    />
);

const ValidationInfoMessage = () => (
    <i data-testid='renter-company-info-message'>
        <FormattedMessage id='intl-msg:rental-web.dialog.start-rental.renter-company-auto-suggestion.validation' />
    </i>
);

const VatLookupWarningMessage = () => (
    <i data-testid='renter-company-vat-lookup-warning-message'>
        <FormattedMessage id='intl-msg:rental-web.dialog.start-rental.renter-company-auto-suggestion.no-vat-account-matches' />
    </i>
);

const PlusAction = ({ children }: { children: React.ReactNode }) => (
    <span className='display-flex'>
        <span className='text-color-primary uikit-icon icon-plus margin-right-5' />
        <span>{children}</span>
    </span>
);

const AddPartnerAction = ({ messageId }: { messageId: string }) => (
    <PlusAction>
        <FormattedMessage id={messageId} />
    </PlusAction>
);

const VatAccountAction = ({ accountName, vatId }: { accountName: string; vatId: string }) => (
    <PlusAction>
        <span className='display-flex align-items-center'>
            <span>{accountName}</span>
            <small className='text-color-gray margin-left-5'>VAT {vatId}</small>
        </span>
    </PlusAction>
);

const PartnerCreationFlow = ({ name, creationFlow }: { name: string; creationFlow: CreationFlow }) => (
    <span className='display-flex align-items-center'>
        <PartnerCreationFlowIcon creationFlow={creationFlow} placement='bottom' className='display-flex' />
        <span className='padding-left-5'>{name}</span>
    </span>
);

const createPartnerSuggestions = (renterPartnerCompanies: RenterPartnerCompany[]): RenterCompanySuggestion[] =>
    renterPartnerCompanies.map(renterPartner => ({
        label: renterPartner.name,
        accountId: renterPartner.accountId,
        creationFlow: renterPartner.creationFlow,
        suggestionType: 'partner',
    }));

const createValidationInfoSuggestion = (): RenterCompanySuggestion => ({
    label: 'validation-info',
    disabled: true,
    suggestionType: 'validation-info',
});

const createVatLookupWarningSuggestion = (): RenterCompanySuggestion => ({
    label: 'vat-lookup-warning',
    disabled: true,
    suggestionType: 'validation-warning',
});

const createAddPartnerDividerSuggestion = (): RenterCompanySuggestion => ({
    label: 'add-partner-divider',
    disabled: true,
    suggestionType: 'add-partner-divider',
});

const createAddPartnerSuggestion = (messageId: string): RenterCompanySuggestion => ({
    label: ADD_PARTNER_ACCOUNT_ID,
    messageId,
    accountId: ADD_PARTNER_ACCOUNT_ID,
    suggestionType: 'add-partner',
});

const appendAddPartnerSuggestion = (
    suggestions: RenterCompanySuggestion[],
    messageId: string
): RenterCompanySuggestion[] => [
    ...suggestions,
    ...(suggestions.length > 0 ? [createAddPartnerDividerSuggestion()] : []),
    createAddPartnerSuggestion(messageId),
];

const createVatAccountSuggestion = (accountProfile: AccountProfile, vatId: string): RenterCompanySuggestion => ({
    label: createVatAccountDisplayValue(vatId, accountProfile.display_name),
    accountId: accountProfile.id,
    accountName: accountProfile.display_name,
    suggestionType: 'vat-account',
    vatId,
});

const normalizeVatId = (value: string) => value.trim().toUpperCase();
const isValidVatId = (value: string) => VAT_ID_PATTERNS.some(pattern => pattern.test(value));
const createVatAccountDisplayValue = (vatId: string, accountName: string) => [accountName, vatId].join(' - ');
const getVatIdForLookup = (value: string, selectedVatAccount: SelectedVatAccount | null) => {
    if (selectedVatAccount && value === selectedVatAccount.displayValue) {
        return selectedVatAccount.vatId;
    }

    const normalizedValue = normalizeVatId(value);
    const possibleVatId = findVatIdInValue(normalizedValue);

    return possibleVatId ?? normalizedValue;
};

const findVatIdInValue = (value: string) => value.split(/\s+|-/).find(isValidVatId);

const findMatchingPartners = (allSuggestions: RenterCompanySuggestion[], value: string) =>
    allSuggestions.filter(renterPartner => renterPartner.label?.toLowerCase().includes(value.toLowerCase()));

const mergePartnerAndVatAccountSuggestions = (
    partnerSuggestions: RenterCompanySuggestion[],
    vatAccountSuggestions: RenterCompanySuggestion[],
    existingPartnerSuggestions: RenterCompanySuggestion[]
) => {
    const suggestions = [...partnerSuggestions];
    const addedAccountIds = new Set(
        suggestions.flatMap(suggestion => (suggestion.accountId ? [String(suggestion.accountId)] : []))
    );

    preferExistingPartnersOverVatAccounts(vatAccountSuggestions, existingPartnerSuggestions).forEach(suggestion => {
        if (suggestion.accountId && addedAccountIds.has(String(suggestion.accountId))) {
            return;
        }

        suggestions.push(suggestion);
        if (suggestion.accountId) {
            addedAccountIds.add(String(suggestion.accountId));
        }
    });

    return suggestions;
};

const preferExistingPartnersOverVatAccounts = (
    vatAccountSuggestions: RenterCompanySuggestion[],
    existingPartnerSuggestions: RenterCompanySuggestion[]
) => {
    const existingPartnerByAccountId = new Map(
        existingPartnerSuggestions.flatMap(existingPartnerSuggestion =>
            existingPartnerSuggestion.accountId
                ? [[String(existingPartnerSuggestion.accountId), existingPartnerSuggestion]]
                : []
        )
    );
    const addedAccountIds = new Set<string>();

    return vatAccountSuggestions.reduce<RenterCompanySuggestion[]>((suggestionsToShow, vatAccountSuggestion) => {
        const accountId = vatAccountSuggestion.accountId;

        if (!accountId || addedAccountIds.has(String(accountId))) {
            return suggestionsToShow;
        }

        suggestionsToShow.push(existingPartnerByAccountId.get(String(accountId)) ?? vatAccountSuggestion);
        addedAccountIds.add(String(accountId));

        return suggestionsToShow;
    }, []);
};

export default RenterCompanyAutoSuggestion;
